import React, { useState } from 'react';
import { DownloadIcon, ArrowUpRightFromSquareIcon } from '@revfluence/fresh-icons/regular/esm';
import { MetaIcon } from '@revfluence/fresh-icons/brands/esm';
import { ChevronDownIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button } from '@/shadcn/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shadcn/components/ui/dropdown-menu';
import { cn } from '@/shadcn/lib/utils';

export interface SheetTopBarBoostActionMenuProps {
  /** Callback for download action */
  onDownload: () => void;
  /** Callback for view post action */
  onViewPost: () => void;
  /** Callback for create boost action */
  onCreateBoost: () => void;
  /** Whether the boost action is available */
  canBoost?: boolean;
  /** Custom label for the boost action */
  boostLabel?: string;
  /** Whether the menu is disabled */
  disabled?: boolean;
  /** Optional additional CSS classes */
  className?: string;
}

/**
 * SheetTopBarBoostActionMenu
 * ---------------------------
 * Composable version of the boost action menu.
 *
 * This component provides download, view post, and create boost actions
 * in a dropdown menu format. It's designed to work with the new composable
 * system and can be used as a child of other sheet top bar components.
 */
const SheetTopBarBoostActionMenu: React.FC<SheetTopBarBoostActionMenuProps> = ({
  onDownload,
  onViewPost,
  onCreateBoost,
  canBoost = true,
  boostLabel = 'Create Boost',
  disabled = false,
  className,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <DropdownMenu onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className={cn('flex items-center gap-2', className)}
          data-dd-action-name="sheet-top-bar-boost-action-menu"
        >
          Actions
          <ChevronDownIcon
            className={cn(
              'h-3 w-3 transition-transform duration-200',
              isDropdownOpen ? 'rotate-180' : ''
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={onDownload}
          className="cursor-pointer"
          disabled={disabled}
          data-dd-action-name="sheet-top-bar-boost-action-menu-download"
          aria-label="Download post"
        >
          <DownloadIcon className="h-3.5 w-3.5 mr-2" />
          Download Post
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={onViewPost}
          className="cursor-pointer"
          disabled={disabled}
          data-dd-action-name="sheet-top-bar-boost-action-menu-view-post"
          aria-label="View post externally"
        >
          <ArrowUpRightFromSquareIcon className="h-3.5 w-3.5 mr-2" />
          View Post
        </DropdownMenuItem>

        <DropdownMenuItem
          onSelect={onCreateBoost}
          className="cursor-pointer"
          disabled={disabled || !canBoost}
          data-dd-action-name="sheet-top-bar-boost-action-menu-create-boost"
          aria-label="Create boost for post"
        >
          <MetaIcon className="h-3.5 w-3.5 mr-2" />
          {boostLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SheetTopBarBoostActionMenu;
